// shared/src/prompts/a-synthesis.ts
// A-stage portfolio cross-ticker synthesis (a.1 in chain V2 naming).
//
// Phase 6 §B (Chain V2 cutover — 2026-05-25) — VERSION 3.0.0:
//   NARROW per design-notes L24. LLM emits ONLY:
//     · cross_ticker_paradoxes
//     · synthesis_prose
//   Dropped (now code-derived in a.1.synthesis chain stage):
//     · bias_distribution     (code: tally portfolio tickers' contrarian.direction)
//     · alignment_per_ticker  (code: compare contrarian.direction vs synthesis.regime, or use arbiter.thesis_align)
//     · asymmetry_ranked      (code: sort portfolio by R:R = |event_horizon_conf_pct|/|event_horizon_inv_pct|)
//   Dropped per L14:
//     · calibration_summary input (no calibration scalars enter LLM context)
//     · CALIBRATION SANITY CHECK section
//
// Inputs at LLM level reduce to:
//   · tickers + macro_v2 + ticker_decisions (portfolio per-ticker outputs only,
//     per L24 — universe-only consumed by portfolio arbiter via cluster-peer
//     mechanism, not here)
//
// Output schema persistence note: the chain stage MERGES code-derived
// bias_distribution / alignment_per_ticker / asymmetry_ranked into the
// brief_payloads.basket + brief_payloads.asymmetry rows. The dashboard zone
// types (BasketPayload / AsymmetryPayload in shared/src/types/brief-payload.ts)
// are UNCHANGED — only the LLM contract narrows.

import { z } from 'zod';
import { llmStringItem } from '../resolvers/llm-string-coerce.js';
import { tolerantRowArray } from '../resolvers/llm-row-drop.js';

export const VERSION = '3.2.0';   // 2026-08-07 — cross_ticker_paradoxes per-ROW omission hardening (tolerantRowArray: drop a paradox row with no description). 2026-07-10 — cross_ticker_paradoxes .default([]) (omission-safe; rule 1 "0 items valid" → default the omitted key). Phase 6 §B narrow per L24 (2026-05-25)
export const STEP = 'a.1.synthesis';
export const MODEL = 'deepseek-v4-flash';
export const MAX_TOKENS = 32000;   // 2026-08-01 blanket truncation-proofing (was 6000)
export const ROLE = 'synthesis' as const;
export const DESC = 'Surfaces cross-ticker paradoxes across the portfolio\'s per-ticker arbiter decisions' as const;   // #45 registry — one-line operator description

export const OutputSchema = z.object({
  // 2026-08-07: per-ROW omission hardening (docs/agreement-matrix-row-omission-hardening-2026-08-07-brief-revision.md).
  // A paradox row with no `description` is noise → DROP it + log (not a brief kill).
  // Omitted WHOLE key still defaults to [] (rule 1: "0 items valid, coherent basket").
  cross_ticker_paradoxes: tolerantRowArray(z.object({
    description: z.string().min(1),
    tickers:     z.array(llmStringItem).optional(),
  }), 'a.1.synthesis.cross_ticker_paradoxes'),
  synthesis_prose: z.string(),
}).passthrough();
export type Output = z.infer<typeof OutputSchema>;

export const SYSTEM = `You are a senior portfolio strategist producing the BASKET-LEVEL CROSS-TICKER SYNTHESIS across N portfolio-ticker arbiter decisions + the refined macro view (m.synthesis). Your output is a narrow read-out: cross-ticker paradoxes the per-ticker view cannot see, plus a brief threaded prose summary.

## ROLE
- Look ACROSS portfolio per-ticker decisions for portfolio-level patterns NOT visible at any single-ticker level.
- Surface CROSS-TICKER PARADOXES: places where two or more tickers' direction calls are INCONSISTENT with a coherent macro thesis (e.g., long crude + short oil services; long high-beta growth + short small-caps).
- Thread a brief prose narrative threading the cross-ticker dynamic + regime divergences.
- Output strict JSON only.

## CONTEXT YOU RECEIVE (user message)
- macro_v2 final synthesis (already refined; reflects tool data; reference only — do NOT restate)
- per-ticker arbiter decisions: { ticker, direction, confidence, thesis_align, ticker_paradox, reasoning }
  (portfolio per-ticker outputs ONLY — universe-only tickers are consumed by the portfolio arbiter via cluster-peer mechanism, not surfaced here per L24)

## NO CALIBRATION (L14)
Reason FRESH from THIS brief's per-ticker decisions + macro_v2. NO past-performance scalars enter your context. Server-side calibration is filter/rank/gate only and never reaches this prompt.

## NO REDUNDANCY WITH CODE-DERIVED FIELDS
The basket dashboard ALSO renders bias_distribution (long/short/flat tally), alignment_per_ticker (per-ticker direction vs regime), and asymmetry_ranked (R:R ordering). Those are CODE-DERIVED downstream from the same per-ticker decisions you receive. You do NOT emit them. Do NOT restate the tally or per-ticker classification in synthesis_prose — instead, thread the CROSS-TICKER mechanism that the tally itself does not communicate.

## OUTPUT SCHEMA (strict JSON; first char='{'; last char='}'; no commentary)
{
  "cross_ticker_paradoxes": [
    { "description": "<≤30w: payload-anchored paradox naming the conflicting tickers + the mechanism>", "tickers": ["<sym1>", "<sym2>"] }
  ],
  "synthesis_prose": "<headline ≤15w. Then 2-3 sentences (60-80w total) that THREAD the portfolio: which cross-ticker dynamic dominates, what regime divergences mean for sizing, what's the one cross-ticker risk to watch. Connected prose, not a list. NO restatement of the tally.>"
}

## RULES
1. cross_ticker_paradoxes: emit ONLY SUBSTANTIVE paradoxes — 0 items is valid if all tickers are coherently aligned with the macro regime. Don't fabricate paradoxes to look thorough; an empty array is the honest answer when the basket is coherent.
2. synthesis_prose follows the headline+expansion pattern: lead sentence ≤15w is a CALL, not a description; expansion cites the specific cross-ticker mechanism + a trigger.
3. NO RESTATING macro_v2 — that's already on the dashboard.
4. NO RESTATING the per-ticker tally or per-ticker direction classification — those render code-derived on the dashboard.
5. ONLY emit what is visible from the CROSS-TICKER cross-section.
`;

export interface ASynthesisInput {
  readonly tickers:           readonly string[];
  readonly macro_v2:          Record<string, unknown>;
  readonly ticker_decisions:  ReadonlyArray<{
    readonly ticker:      string;
    readonly direction:   string;
    readonly confidence?: number;
    // S-50: `action` dropped — V3 arbiter is state-only, always rendered `action=not set`.
    readonly thesis_align?: string;
    readonly ticker_paradox?: string;
    readonly reasoning?:  string;
  }>;
}

function fmt(v: unknown): string {
  if (v == null) return 'not set';
  if (typeof v === 'string') return v.length === 0 ? 'not set' : v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

// S-05 (F9b-9): whitelist aligned to the m.synthesis emit shape (m-synthesis.ts
// OutputSchema). The prior list carried `market_wrong` / `mw_thesis` — dead V1
// macro_context fields absent from m.synthesis, so they rendered `not set`. These
// six all exist on m_synthesis and carry the refined macro anchor this prompt is
// built around (regime + confidence + the headline call + the one-sentence crux +
// top risk + status).
function macroExcerpt(m: Record<string, unknown>): string {
  const fields = ['regime', 'regime_confidence', 'thesis_headline', 'crux', 'top_risk', 'thesis_status'];
  return fields.map((f) => `  ${f}: ${fmt(m[f])}`).join('\n');
}

function tickerRow(d: ASynthesisInput['ticker_decisions'][number]): string {
  return `  ${d.ticker}: direction=${fmt(d.direction)} · confidence=${fmt(d.confidence)} · thesis_align=${fmt(d.thesis_align)} · ticker_paradox="${fmt(d.ticker_paradox)}"`;
}

export function buildUserPrompt(input: ASynthesisInput): string {
  return `TICKERS (portfolio only): ${input.tickers.join(', ')}

MACRO V2 (already published as macro_payload; reference only — do NOT restate):
${macroExcerpt(input.macro_v2)}

PER-TICKER DECISIONS (${input.ticker_decisions.length} rows):
${input.ticker_decisions.map(tickerRow).join('\n')}

REQUEST: emit_basket_cross_ticker_synthesis

Emit a SINGLE JSON object matching the schema. cross_ticker_paradoxes is 0+ items (emit only SUBSTANTIVE paradoxes). synthesis_prose follows the headline+expansion rule; NO restating the per-ticker tally (code derives that downstream).`;
}
